import { useRef, useState, type DragEvent } from "react";
import { Upload, X } from "lucide-react";
import { cn, validateFile } from "@/lib/utils";
import { appConfig } from "@/config/app";
import { useTranslation } from "react-i18next";

interface FileUploadFieldProps {
  file: File | null;
  onFileSelect: (file: File | null) => void;
  onError: (message: string) => void;
  className?: string;
  /** Blocks picking/dropping a file, e.g. before a required upstream selection is made. */
  disabled?: boolean;
}

export function FileUploadField({
  file,
  onFileSelect,
  onError,
  className,
  disabled = false,
}: FileUploadFieldProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (selected: File) => {
    const result = validateFile(selected);
    if (!result.valid) {
      onError(result.error ?? "Invalid file");
      return;
    }
    onError("");
    onFileSelect(selected);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
    // Reset so same file can be re-selected
    e.target.value = "";
  };

  const clearFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileSelect(null);
  };

  return (
    <div className={cn("w-full max-w-lg", className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => !disabled && e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "relative flex items-center gap-3 rounded-lg border bg-white px-4 py-3 transition-all duration-200 hover:shadow-sm",
          disabled ? "cursor-not-allowed opacity-60 hover:shadow-none" : "cursor-pointer",
          isDragging
            ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-surface)] ring-2 ring-[var(--color-brand-primary)]/20"
            : "border-[var(--color-border)] hover:border-[var(--color-brand-primary)]",
          file && "border-[var(--color-brand-primary)]"
        )}
      >
        {file ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-button-primary-bg)] px-3 py-1.5 text-sm font-medium text-white truncate max-w-[85%]">
              {file.name}
            </span>
            <button
              onClick={clearFile}
              className="ml-auto p-1 rounded-full hover:bg-[var(--color-bg-tertiary)] transition-colors"
              aria-label="Remove file"
            >
              <X className="h-4 w-4 text-[var(--color-text-secondary)]" />
            </button>
          </>
        ) : (
          <>
            <span className="text-[var(--color-text-tertiary)] text-sm flex-1">
              {t("upload.filePlaceholder")}
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[var(--color-button-primary-bg)] text-white shrink-0">
              <Upload className="h-4 w-4" />
            </span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={appConfig.upload.acceptedTypes.join(",")}
          onChange={handleInputChange}
          disabled={disabled}
          className="sr-only"
          data-testid="file-input"
        />
      </div>
      <p className="mt-1.5 text-xs text-[var(--color-text-tertiary)]">
        {t("upload.acceptedTypes")}
      </p>
    </div>
  );
}
